"use client"

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from "react"
import { useRouter, usePathname } from "next/navigation"
import type { Conversation, ChatMessage } from "./types"
import { type NavContext } from "./tools"
import { useChatHotkey } from "./use-chat-hotkey"
import { fetchApi } from "@/lib/api/client"
import { API_CONFIG } from "@/lib/api/config"

// ─── Streaming tool-call shape (lightweight, for UI only) ────────────────────
export interface StreamingToolCall {
  toolName: string
  status: "executing" | "Completed" | "error"
  success?: boolean
  summary?: string
}

export type CompanionMode = "closed" | "pip" | "drawer" | "maximized"

interface ChatRequestOwner {
  token: symbol
  abort: AbortController
}

// ─── Context value ───────────────────────────────────────────────────────────
export interface ChatContextValue {
  // Conversations
  conversations: Conversation[]
  activeId: string | null
  fetchConversations: (search?: string) => void
  loadConversation: (id: string) => void
  handleNewChat: () => void
  handleDelete: (id?: string) => void
  handleRename: (title: string) => void
  handleTogglePin: (id?: string) => void
  handleToggleStar: (id?: string) => void

  // Messages
  messages: ChatMessage[]
  inputValue: string
  setInputValue: (v: string) => void
  sendMessage: (text: string) => Promise<void>
  handleStop: () => void
  handleRegenerate: () => void
  handleEditMessage: (id: string) => void

  // Streaming
  isStreaming: boolean
  streamingContent: string
  streamingToolCalls: StreamingToolCall[]

  // Utility
  handleExport: () => void
  handleCopyAll: () => void
  handleSuggestion: (text: string) => void
  apiKeyMissing: boolean

  // Verification
  verificationOpen: boolean
  pendingVerificationTool: string
  pendingVerificationInputRef: MutableRefObject<Record<string, unknown>>
  pendingVerificationRequirement: 'confirm' | 'step-up'
  pendingVerificationFactors: Array<'passkey' | 'password'>
  pendingVerificationReason: string
  pendingVerificationMessage: string
  handleVerified: () => void
  handleVerificationCancel: () => void

  // Chat sidebar (within the chat panel)
  chatSidebarOpen: boolean
  setChatSidebarOpen: (v: boolean | ((prev: boolean) => boolean)) => void

  // Companion mode
  companionMode: CompanionMode
  setCompanionMode: (mode: CompanionMode) => void

  // Drawer width (for docked mode)
  drawerWidth: number
  setDrawerWidth: (w: number) => void

  // Auto-navigation
  autoNavigate: boolean
  setAutoNavigate: (v: boolean) => void

  // Step-up auth token (verify-tier tools — valid for 5 min)
  stepUpToken: string | null
  stepUpTokenExpiresAt: string | null
  setStepUpToken: (token: string, expiresAt: string) => void
  clearStepUpToken: () => void
}

export const ChatContext = createContext<ChatContextValue | null>(null)

export function useChatContext() {
  const ctx = useContext(ChatContext)
  if (!ctx) throw new Error("useChatContext must be used within ChatProvider")
  return ctx
}

// ─── Provider ────────────────────────────────────────────────────────────────
export function ChatProvider({ children }: { children: ReactNode }) {
  const router = useRouter()
  const pathname = usePathname()

  // ── Conversations ────────────────────────────────────────────────────────
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [activeId, _setActiveId] = useState<string | null>(null)

  // Hydrate activeId from localStorage after mount
  useEffect(() => {
    const saved = localStorage.getItem("ai-chat-active-id")
    if (saved) _setActiveId(saved)
  }, [])

  // Wrap setActiveId to always persist to localStorage
  const setActiveId = useCallback((id: string | null) => {
    _setActiveId(id)
    if (typeof window !== "undefined") {
      if (id) {
        localStorage.setItem("ai-chat-active-id", id)
      } else {
        localStorage.removeItem("ai-chat-active-id")
      }
    }
  }, [])

  // ── Messages ─────────────────────────────────────────────────────────────
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState("")

  // ── Streaming ────────────────────────────────────────────────────────────
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingContent, setStreamingContent] = useState("")
  const [streamingToolCalls, setStreamingToolCalls] = useState<StreamingToolCall[]>([])
  const requestOwnerRef = useRef<ChatRequestOwner | null>(null)
  const toolCallsRef = useRef<StreamingToolCall[]>([])

  useEffect(() => () => {
    const owner = requestOwnerRef.current
    requestOwnerRef.current = null
    owner?.abort.abort()
  }, [])

  // ── Verification ─────────────────────────────────────────────────────────
  const [verificationOpen, setVerificationOpen] = useState(false)
  const [pendingVerificationTool, setPendingVerificationTool] = useState("")
  const pendingVerificationInputRef = useRef<Record<string, unknown>>({})
  const [pendingVerificationRequirement, setPendingVerificationRequirement] = useState<'confirm' | 'step-up'>('confirm')
  const [pendingVerificationFactors, setPendingVerificationFactors] = useState<Array<'passkey' | 'password'>>([])
  const [pendingVerificationReason, setPendingVerificationReason] = useState<string>('')
  const [pendingVerificationMessage, setPendingVerificationMessage] = useState<string>('')

  // ── Deferred navigation ──────────────────────────────────────────────────
  // Accumulate navigate events during streaming; execute only the final one
  // when the stream ends. Explicit navigate_to_page always wins over implicit.
  const pendingNavRef = useRef<{
    path: string
    context?: NavContext
    explicit: boolean
  } | null>(null)

  // ── Chat sidebar (inside chat panel) ─────────────────────────────────────
  const [chatSidebarOpen, setChatSidebarOpen] = useState(true)

  // Hydrate chatSidebarOpen from localStorage after mount
  const chatSidebarHydrated = useRef(false)
  useEffect(() => {
    if (!chatSidebarHydrated.current) {
      chatSidebarHydrated.current = true
      const saved = localStorage.getItem("ai-chat-sidebar")
      if (saved !== null) setChatSidebarOpen(saved === "true")
    }
  }, [])

  useEffect(() => {
    localStorage.setItem("ai-chat-sidebar", String(chatSidebarOpen))
  }, [chatSidebarOpen])

  // ── Companion mode ───────────────────────────────────────────────────────
  const [companionMode, setCompanionMode] = useState<CompanionMode>("closed")
  const companionModeHydrated = useRef(false)

  // Hydrate from localStorage after mount to avoid SSR/client mismatch
  useEffect(() => {
    if (!companionModeHydrated.current) {
      companionModeHydrated.current = true
      const saved = localStorage.getItem("ai-chat-companion-mode")
      if (saved === "pip" || saved === "drawer" || saved === "maximized") {
        setCompanionMode(saved)
      }
    }
  }, [])

  useEffect(() => {
    localStorage.setItem("ai-chat-companion-mode", companionMode)
  }, [companionMode])

  // ── Global Cmd+K hotkey to toggle the chat drawer ────────────────────────
  useChatHotkey(setCompanionMode, companionMode)

  // ── Drawer width ────────────────────────────────────────────────────────
  const DRAWER_DEFAULT = 420
  const DRAWER_MIN = 320
  const DRAWER_MAX = 700
  const [drawerWidth, setDrawerWidthRaw] = useState(DRAWER_DEFAULT)
  const setDrawerWidth = useCallback((w: number) => {
    const clamped = Math.min(DRAWER_MAX, Math.max(DRAWER_MIN, w))
    setDrawerWidthRaw(clamped)
    localStorage.setItem("ai-chat-drawer-width", String(clamped))
  }, [])

  // Hydrate drawer width
  useEffect(() => {
    const saved = localStorage.getItem("ai-chat-drawer-width")
    if (saved) {
      const n = parseInt(saved, 10)
      if (!isNaN(n)) setDrawerWidthRaw(Math.min(DRAWER_MAX, Math.max(DRAWER_MIN, n)))
    }
  }, [])

  // Close chat when feedback widget opens (cross-widget coordination)
  useEffect(() => {
    const handler = () => setCompanionMode("closed")
    window.addEventListener("widget:close-chat", handler)
    return () => window.removeEventListener("widget:close-chat", handler)
  }, [])

  // ── Auto-navigation ──────────────────────────────────────────────────────
  const [autoNavigate, setAutoNavigate] = useState(true)

  // Hydrate autoNavigate from localStorage after mount
  const autoNavigateHydrated = useRef(false)
  useEffect(() => {
    if (!autoNavigateHydrated.current) {
      autoNavigateHydrated.current = true
      const saved = localStorage.getItem("ai-chat-auto-navigate")
      if (saved !== null) setAutoNavigate(saved === "true")
    }
  }, [])

  useEffect(() => {
    localStorage.setItem("ai-chat-auto-navigate", String(autoNavigate))
  }, [autoNavigate])

  const [apiKeyMissing, setApiKeyMissing] = useState(false)

  // ── Step-up auth token ───────────────────────────────────────────────────
  const [stepUpToken, setStepUpTokenState] = useState<string | null>(null)
  const [stepUpTokenExpiresAt, setStepUpExpiresAt] = useState<string | null>(null)
  const stepUpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // FIX: render-flush race in step-up flow
  // Root cause: sendMessage is memoized and captures stepUpToken from React state.
  // When setStepUpToken() is called, it schedules a state update but the render
  // hasn't committed yet. If sendMessage fires on the same tick (as it does from
  // verification-dialog.tsx after stepUpVerify succeeds), its closure captures
  // the OLD (null) token, causing the BE to return verification_required again.
  // Fix: keep a ref mirror that is written synchronously BEFORE the state update.
  // sendMessage reads from the ref — no render boundary dependency.
  // Date: 2026-05-05
  const stepUpTokenRef = useRef<string | null>(null)

  const setStepUpToken = useCallback((token: string, expiresAt: string) => {
    // Cancel any existing auto-clear timer before installing a new token
    if (stepUpTimerRef.current !== null) {
      clearTimeout(stepUpTimerRef.current)
      stepUpTimerRef.current = null
    }
    // Write ref first (synchronous) so the token is visible to sendMessage
    // immediately, without waiting for React to commit the state update.
    stepUpTokenRef.current = token
    setStepUpTokenState(token)
    setStepUpExpiresAt(expiresAt)
    const ttl = new Date(expiresAt).getTime() - Date.now()
    if (ttl > 0) {
      stepUpTimerRef.current = setTimeout(() => {
        stepUpTokenRef.current = null
        setStepUpTokenState(null)
        setStepUpExpiresAt(null)
        stepUpTimerRef.current = null
      }, ttl)
    }
  }, [])

  const clearStepUpToken = useCallback(() => {
    if (stepUpTimerRef.current !== null) {
      clearTimeout(stepUpTimerRef.current)
      stepUpTimerRef.current = null
    }
    stepUpTokenRef.current = null
    setStepUpTokenState(null)
    setStepUpExpiresAt(null)
  }, [])

  // ── Fetch conversations ──────────────────────────────────────────────────
  const fetchConversations = useCallback(async (search?: string) => {
    if (requestOwnerRef.current) return
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : ""
      const data = await fetchApi<{ conversations?: Conversation[] }>(`/ai-chat/conversations${params}`)
      if (requestOwnerRef.current) return
      setConversations(data.conversations || [])
    } catch {
      // silent
    }
  }, [])

  // Defer initial conversation fetch until the chat companion is opened,
  // so pages that don't use the chat (e.g. /settings) load faster. After
  // first fetch, refresh on every closed → open transition so the drawer
  // never renders against stale-empty state on subsequent opens.
  const hasFetchedConversations = useRef(false)
  const prevCompanionMode = useRef<CompanionMode>("closed")
  useEffect(() => {
    const wasClosed = prevCompanionMode.current === "closed"
    const isOpenNow = companionMode !== "closed"
    prevCompanionMode.current = companionMode
    if (isOpenNow && (wasClosed || !hasFetchedConversations.current)) {
      hasFetchedConversations.current = true
      fetchConversations()
    }
  }, [companionMode, fetchConversations])

  // ── Auto-reload active conversation on mount (survives page refresh) ────
  const hasRehydrated = useRef(false)
  useEffect(() => {
    if (hasRehydrated.current) return
    hasRehydrated.current = true
    if (activeId && messages.length === 0 && !isStreaming) {
      // Rehydrate messages from backend
      fetchApi<{ messages?: ChatMessage[] }>(`/ai-chat/conversations/${activeId}`)
        .then((data) => {
          if (data?.messages?.length) {
            setMessages(data.messages)
          }
        })
        .catch(() => {
          // Conversation may have been deleted — clear stale activeId
          setActiveId(null)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Load conversation ────────────────────────────────────────────────────
  const loadConversation = useCallback(async (id: string) => {
    if (requestOwnerRef.current) return
    try {
      const data = await fetchApi<{ messages?: ChatMessage[] }>(`/ai-chat/conversations/${id}`)
      if (requestOwnerRef.current) return
      setMessages(data.messages || [])
      setActiveId(id)
    } catch {
      // silent
    }
  }, [])

  // ── New chat ─────────────────────────────────────────────────────────────
  const handleNewChat = useCallback(async () => {
    if (requestOwnerRef.current) return
    try {
      const conv = await fetchApi<Conversation>("/ai-chat/conversations", {
        method: "POST",
        body: JSON.stringify({}),
      })
      if (requestOwnerRef.current) return
      setConversations((prev) => [conv, ...prev])
      setActiveId(conv.id)
      setMessages([])
      setStreamingContent("")
      setStreamingToolCalls([])
    } catch {
      // silent
    }
  }, [])

  // ── Send message (SSE streaming) ─────────────────────────────────────────
  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || requestOwnerRef.current) return

      const owner: ChatRequestOwner = {
        token: Symbol("chat-request"),
        abort: new AbortController(),
      }
      requestOwnerRef.current = owner
      setIsStreaming(true)
      setStreamingContent("")
      setStreamingToolCalls([])
      toolCallsRef.current = []
      pendingNavRef.current = null
      setApiKeyMissing(false)
      const ownsRequest = () => requestOwnerRef.current === owner
      const mayWrite = () => ownsRequest() && !owner.abort.signal.aborted
      let conversationId = activeId

      // ── Streamed-answer state ────────────────────────────────────────────
      // Declared OUTSIDE the try so the catch/abort paths can still commit
      // what was received.
      //
      // The assistant message used to be appended ONLY inside `case "done"`.
      // Any stream that ended without that frame — server restart, proxy
      // timeout, dropped connection, mid-stream error — silently discarded
      // everything already streamed: the user watched an answer appear on
      // screen and then vanish, with no error and nothing persisted.
      //
      // `committed` + commitAssistant() make the rule explicit: the text we
      // showed is the text we keep, on every exit path, exactly once.
      let fullText = ""
      let committed = false
      const commitAssistant = () => {
        if (committed || !ownsRequest()) return
        committed = true
        if (!fullText && toolCallsRef.current.length === 0) return
        const toolCalls =
          toolCallsRef.current.length > 0
            ? toolCallsRef.current.map((tc, i) => ({
                id: `tc-${Date.now()}-${i}`,
                toolName: tc.toolName,
                input: {},
                status: (tc.status === "Completed" ? "Completed" : "error") as
                  | "Completed"
                  | "error",
                result: {
                  success: tc.success ?? true,
                  data: undefined,
                  error: tc.success === false ? tc.summary : undefined,
                },
                summary: tc.summary,
              }))
            : undefined
        const assistantMsg: ChatMessage = {
          id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          role: "assistant",
          content: fullText,
          toolCalls,
          createdAt: new Date().toISOString(),
        }
        setMessages((prev) => [...prev, assistantMsg])
      }

      try {
        if (!conversationId) {
          const conv = await fetchApi<Conversation>("/ai-chat/conversations", {
            method: "POST",
            body: JSON.stringify({ title: text.slice(0, 60) }),
            signal: owner.abort.signal,
          })
          if (!mayWrite()) return
          setConversations((prev) => [conv, ...prev])
          setActiveId(conv.id)
          conversationId = conv.id
        }

        if (!mayWrite()) return
        const userMsg: ChatMessage = {
          id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          role: "user",
          content: text,
          createdAt: new Date().toISOString(),
        }
        setMessages((prev) => [...prev, userMsg])
        setInputValue("")

        // NOTE: Raw fetch() is intentional here — this is an SSE streaming
        // endpoint that returns a ReadableStream, not JSON. fetchApi() auto-parses
        // JSON responses and cannot handle streaming bodies.
        const streamUrl = `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}/ai-chat/stream`

        // Build headers — auth + workspace headers required by the BE.
        const streamHeaders: Record<string, string> = { "Content-Type": "application/json" }
        const token = localStorage.getItem("hf-app:access-token")
        if (token) streamHeaders["Authorization"] = `Bearer ${token}`
        const wsId = localStorage.getItem("hf-app:workspace-id")
        if (wsId) streamHeaders["x-workspace-id"] = wsId

        // Read from ref (not captured state) to get the token written by
        // setStepUpToken on the same synchronous tick — no render flush needed.
        const attachedStepUpToken = stepUpTokenRef.current

        // One-shot on the client too, mirroring the server.
        //
        // A token is bound to ONE tool + input and the executor burns its jti on
        // use, so it can never authorise a second action. Holding it for the full
        // 5-minute TTL and attaching it to every later message therefore cannot
        // help — but it does harm: the next destructive request ships a token the
        // server can only answer with APPROVAL_MISMATCH (different action) or
        // APPROVAL_ALREADY_USED (same one, spent). Both are hard errors rather
        // than a fresh verification prompt, so the user was dead-ended out of
        // every destructive action until the TTL expired.
        //
        // Clearing it as it goes out means the next such request arrives with no
        // token and gets the verification prompt it should have got all along.
        if (attachedStepUpToken) clearStepUpToken()

        const res = await fetch(streamUrl, {
          method: "POST",
          headers: streamHeaders,
          body: JSON.stringify({
            conversationId,
            message: text,
            ...(attachedStepUpToken ? { stepUpToken: attachedStepUpToken } : {}),
          }),
          signal: owner.abort.signal,
        })

        if (!mayWrite()) return

        if (!res.ok) {
          const errData = await res.json().catch(() => ({ error: "Request failed" }))
          if (!mayWrite()) return
          if (res.status === 503) setApiKeyMissing(true)
          const errMsg: ChatMessage = {
            id: `msg-${Date.now()}-err`,
            role: "assistant",
            content: `**Error:** ${errData.error || "Something went wrong."}`,
            createdAt: new Date().toISOString(),
          }
          setMessages((prev) => [...prev, errMsg])
          return
        }

        const reader = res.body?.getReader()
        if (!reader) return

        const decoder = new TextDecoder()
        let buffer = ""

        while (true) {
          const { done, value } = await reader.read()
          if (!mayWrite()) return
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split("\n")
          buffer = lines.pop() || ""

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue
            const jsonStr = line.slice(6).trim()
            if (!jsonStr) continue

            try {
              const event = JSON.parse(jsonStr)

              switch (event.type) {
                case "text":
                  fullText += event.content
                  setStreamingContent(fullText)
                  break

                case "tool_call": {
                  const newTc = { toolName: event.toolName, status: "executing" as const }
                  toolCallsRef.current = [...toolCallsRef.current, newTc]
                  setStreamingToolCalls((prev) => [...prev, newTc])
                  break
                }

                case "tool_result": {
                  toolCallsRef.current = toolCallsRef.current.map((tc) =>
                    tc.toolName === event.toolName && tc.status === "executing"
                      ? { ...tc, status: "Completed" as const, success: event.success, summary: event.summary }
                      : tc
                  )
                  setStreamingToolCalls((prev) =>
                    prev.map((tc) =>
                      tc.toolName === event.toolName && tc.status === "executing"
                        ? { ...tc, status: "Completed", success: event.success, summary: event.summary }
                        : tc
                    )
                  )
                  break
                }

                case "done": {
                  commitAssistant()
                  setStreamingContent("")
                  setStreamingToolCalls([])
                  toolCallsRef.current = []

                  // Execute deferred navigation — single router.push at stream end
                  const nav = pendingNavRef.current
                  if (nav) {
                    pendingNavRef.current = null
                    const navCtx = nav.context

                    if (pathname !== nav.path) {
                      // Cross-page: write context to sessionStorage (consumed
                      // once on the target page mount, then deleted).
                      if (navCtx) {
                        if (navCtx.columnFilters?.length) {
                          sessionStorage.setItem(
                            "ai-nav-column-filters",
                            JSON.stringify(navCtx.columnFilters)
                          )
                        }
                        if (navCtx.globalFilter) {
                          sessionStorage.setItem("ai-nav-global-filter", navCtx.globalFilter)
                        }
                        if (navCtx.focusEntity) {
                          sessionStorage.setItem(
                            "ai-nav-focus",
                            JSON.stringify(navCtx.focusEntity)
                          )
                        }
                      }
                      setCompanionMode("pip")
                      router.push(nav.path)
                    } else if (navCtx) {
                      // Same-page: dispatch event with context directly attached
                      window.dispatchEvent(
                        new CustomEvent("ai-nav-context", { detail: navCtx })
                      )
                    }
                  }
                  break
                }

                case "verification_required": {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const vEvent = event as any
                  setPendingVerificationTool(vEvent.toolName)
                  pendingVerificationInputRef.current = vEvent.input || {}
                  setPendingVerificationRequirement(vEvent.requirement ?? 'confirm')
                  setPendingVerificationFactors(vEvent.factors ?? [])
                  setPendingVerificationReason(vEvent.verifyReason ?? vEvent.message ?? '')
                  setPendingVerificationMessage(vEvent.message ?? '')
                  setVerificationOpen(true)
                  break
                }

                case "navigate": {
                  const targetPath = event.path as string
                  const navContext = event.context as NavContext | undefined
                  const isExplicit = event.explicit === true

                  // Accumulate navigate events — execute only the final one when
                  // the stream ends. Explicit navigate_to_page always takes
                  // priority; among implicit navigations the last one wins.
                  // Storage writes are deferred to the "done" handler to avoid
                  // polluting localStorage with intermediate tool contexts.
                  if (autoNavigate && targetPath) {
                    const pending = pendingNavRef.current
                    if (isExplicit || !pending?.explicit) {
                      pendingNavRef.current = { path: targetPath, context: navContext, explicit: isExplicit }
                    }
                  }
                  break
                }

                case "error": {
                  // Keep whatever already streamed. Discarding it left the
                  // user with only "Error:" and no way to tell whether the
                  // work described in the partial answer had happened.
                  commitAssistant()
                  setStreamingContent("")
                  setStreamingToolCalls([])
                  toolCallsRef.current = []
                  const errMsg: ChatMessage = {
                    id: `msg-${Date.now()}-err`,
                    role: "assistant",
                    content: `**Error:** ${event.message}`,
                    createdAt: new Date().toISOString(),
                  }
                  setMessages((prev) => [...prev, errMsg])
                  break
                }
              }
            } catch {
              // malformed SSE line
            }
          }
        }
        // The reader closed. If no `done` frame ever arrived — server restart,
        // proxy timeout, dropped connection — the answer is still in fullText
        // and was previously discarded here without a trace.
        if (!committed && (fullText || toolCallsRef.current.length > 0)) {
          commitAssistant()
          setMessages((prev) => [
            ...prev,
            {
              id: `msg-${Date.now()}-trunc`,
              role: "assistant",
              content:
                "*[The connection closed before this answer finished — the text above is what was received.]*",
              createdAt: new Date().toISOString(),
            },
          ])
        }
      } catch (error) {
        if (!ownsRequest()) return
        // Preserve the partial answer on BOTH paths. This is the SINGLE writer
        // for the stopped case — handleStop only aborts, because having both
        // write appended the same text twice.
        const aborted = owner.abort.signal.aborted || (error as Error).name === "AbortError"
        const hadText = !committed && (fullText || toolCallsRef.current.length > 0)
        commitAssistant()
        if (aborted) {
          if (hadText) {
            setMessages((prev) => [
              ...prev,
              {
                id: `msg-${Date.now()}-stopped`,
                role: "assistant",
                content: "*[Generation stopped]*",
                createdAt: new Date().toISOString(),
              },
            ])
          }
        } else {
          const errMsg: ChatMessage = {
            id: `msg-${Date.now()}-err`,
            role: "assistant",
            content: "**Error:** Connection lost. Please try again.",
            createdAt: new Date().toISOString(),
          }
          setMessages((prev) => [...prev, errMsg])
        }
      } finally {
        if (requestOwnerRef.current === owner) {
          requestOwnerRef.current = null
          setIsStreaming(false)
          setStreamingContent("")
          setStreamingToolCalls([])
          toolCallsRef.current = []
          pendingNavRef.current = null
        }
      }
    },
    // stepUpToken intentionally omitted — sendMessage reads from stepUpTokenRef
    // (synchronous, always current) not from the captured state variable.
    // clearStepUpToken IS listed: it is stable (useCallback with []), so it adds
    // no re-creation churn, and listing it keeps exhaustive-deps honest.
    [
      activeId,
      router,
      pathname,
      autoNavigate,
      clearStepUpToken,
    ]
  )

  // ── Stop streaming ───────────────────────────────────────────────────────
  const handleStop = useCallback(() => {
    // Abort ONLY. The stream's catch block commits the partial answer and
    // appends the "[Generation stopped]" note. Writing here as well produced
    // the answer twice once the abort path started preserving it.
    requestOwnerRef.current?.abort.abort()
  }, [])

  // ── Regenerate ───────────────────────────────────────────────────────────
  const handleRegenerate = useCallback(() => {
    if (requestOwnerRef.current) return
    const lastUserIdx = [...messages].reverse().findIndex((m) => m.role === "user")
    if (lastUserIdx === -1) return
    const idx = messages.length - 1 - lastUserIdx
    const lastUserMsg = messages[idx]
    setMessages(messages.slice(0, idx))
    sendMessage(lastUserMsg.content)
  }, [messages, sendMessage])

  // ── Edit & resend ────────────────────────────────────────────────────────
  const handleEditMessage = useCallback(
    (messageId: string) => {
      if (requestOwnerRef.current) return
      const msgIdx = messages.findIndex((m) => m.id === messageId)
      if (msgIdx === -1) return
      const msg = messages[msgIdx]
      setInputValue(msg.content)
      if (activeId) {
        fetchApi(`/ai-chat/conversations/${activeId}`, {
          method: "PATCH",
          body: JSON.stringify({}),
        }).catch(() => {/* silent */})
      }
      setMessages(messages.slice(0, msgIdx))
    },
    [messages, activeId]
  )

  // ── Conversation actions ─────────────────────────────────────────────────
  const handleRename = useCallback(
    async (title: string) => {
      if (!activeId || requestOwnerRef.current) return
      await fetchApi(`/ai-chat/conversations/${activeId}`, {
        method: "PATCH",
        body: JSON.stringify({ title }),
      })
      if (!requestOwnerRef.current) fetchConversations()
    },
    [activeId, fetchConversations]
  )

  const handleTogglePin = useCallback(
    async (id?: string) => {
      if (requestOwnerRef.current) return
      const targetId = id || activeId
      if (!targetId) return
      const conv = conversations.find((c) => c.id === targetId)
      if (!conv) return
      await fetchApi(`/ai-chat/conversations/${targetId}`, {
        method: "PATCH",
        body: JSON.stringify({ isPinned: !conv.isPinned }),
      })
      if (!requestOwnerRef.current) fetchConversations()
    },
    [activeId, conversations, fetchConversations]
  )

  const handleToggleStar = useCallback(
    async (id?: string) => {
      if (requestOwnerRef.current) return
      const targetId = id || activeId
      if (!targetId) return
      const conv = conversations.find((c) => c.id === targetId)
      if (!conv) return
      await fetchApi(`/ai-chat/conversations/${targetId}`, {
        method: "PATCH",
        body: JSON.stringify({ isStarred: !conv.isStarred }),
      })
      if (!requestOwnerRef.current) fetchConversations()
    },
    [activeId, conversations, fetchConversations]
  )

  const handleDelete = useCallback(
    async (id?: string) => {
      if (requestOwnerRef.current) return
      const targetId = id || activeId
      if (!targetId) return
      await fetchApi(`/ai-chat/conversations/${targetId}`, { method: "DELETE" })
      if (requestOwnerRef.current) return
      if (targetId === activeId) {
        setActiveId(null)
        setMessages([])
      }
      fetchConversations()
    },
    [activeId, fetchConversations]
  )

  const handleExport = useCallback(() => {
    if (!messages.length) return
    const activeConv = conversations.find((c) => c.id === activeId)
    const md = messages
      .map((m) => `**${m.role === "user" ? "You" : "Assistant"}:**\n\n${m.content}`)
      .join("\n\n---\n\n")
    const blob = new Blob([md], { type: "text/markdown" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${activeConv?.title || "chat"}.md`
    a.click()
    URL.revokeObjectURL(url)
  }, [messages, conversations, activeId])

  const handleCopyAll = useCallback(() => {
    const text = messages
      .map((m) => `${m.role === "user" ? "You" : "Assistant"}: ${m.content}`)
      .join("\n\n")
    navigator.clipboard.writeText(text)
  }, [messages])

  const handleSuggestion = useCallback(
    (text: string) => {
      sendMessage(text)
    },
    [sendMessage]
  )

  // ── Verification callbacks ───────────────────────────────────────────────
  const handleVerified = useCallback(() => {
    // FIX: remove verifiedToolCall FE plumbing (BE removed field in Wave 2).
    // The BE's confirm-tier executor now reads input.confirmed===true from the
    // LLM's next tool-call attempt; step-up flows already use stepUpToken in
    // the request body. Sending the natural-language prompt re-triggers the
    // LLM to call the same tool with the confirmed/stepUpToken context present.
    setVerificationOpen(false)
    sendMessage(
      `I have verified my identity. Please proceed with the ${pendingVerificationTool.replace(/_/g, " ")} operation.`
    )
  }, [pendingVerificationTool, sendMessage])

  const handleVerificationCancel = useCallback(() => {
    setVerificationOpen(false)
    setPendingVerificationTool("")
    sendMessage("I cancelled the verification. Do not proceed with the destructive operation.")
  }, [sendMessage])

  // ── Context value ────────────────────────────────────────────────────────
  const value: ChatContextValue = {
    conversations,
    activeId,
    fetchConversations,
    loadConversation,
    handleNewChat,
    handleDelete,
    handleRename,
    handleTogglePin,
    handleToggleStar,

    messages,
    inputValue,
    setInputValue,
    sendMessage,
    handleStop,
    handleRegenerate,
    handleEditMessage,

    isStreaming,
    streamingContent,
    streamingToolCalls,

    handleExport,
    handleCopyAll,
    handleSuggestion,
    apiKeyMissing,

    verificationOpen,
    pendingVerificationTool,
    pendingVerificationInputRef,
    pendingVerificationRequirement,
    pendingVerificationFactors,
    pendingVerificationReason,
    pendingVerificationMessage,
    handleVerified,
    handleVerificationCancel,

    chatSidebarOpen,
    setChatSidebarOpen,

    companionMode,
    setCompanionMode,

    drawerWidth,
    setDrawerWidth,

    autoNavigate,
    setAutoNavigate,

    stepUpToken,
    stepUpTokenExpiresAt,
    setStepUpToken,
    clearStepUpToken,
  }

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}
